import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getCommandes, getCommande, createCommande, updateCommande, deleteCommande,
  envoyerCommande, recevoirCommande, annulerCommande,
  getFournisseurs, getProduits, getEntrepots, getBoutiques,
} from "../services/api";
import {
  Plus, Edit2, Trash2, Send, Package, X, Filter,
  AlertCircle, CheckCircle, Loader, Check,
  ShoppingCart, Clock, TrendingUp, Ban, ChevronRight, Search
} from "lucide-react";
import moment from "moment";
import "moment/locale/fr";
import { useAuth } from "../context/AuthContext";
import BoutiqueSelector from "../components/BoutiqueSelector";
import { useBoutique } from "../context/BoutiqueContext";
import "../styles/CommandesFournisseurs.css";

moment.locale("fr");

const fmt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0);

// Texte normalisé pour la recherche : minuscules, sans accents
const normaliser = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const STATUT_CONFIG = {
  brouillon:        { label: "Brouillon",            color: "#64748b", bg: "#f1f5f9" },
  envoyee:          { label: "Envoyée",               color: "#2563eb", bg: "#eff6ff" },
  livree_partielle: { label: "Livrée partiellement", color: "#d97706", bg: "#fffbeb" },
  livree:           { label: "Livrée",                color: "#16a34a", bg: "#dcfce7" },
  annulee:          { label: "Annulée",               color: "#dc2626", bg: "#fee2e2" },
};

const StatutBadge = ({ statut }) => {
  const c = STATUT_CONFIG[statut] || STATUT_CONFIG.brouillon;
  return (
    <span className="cf-statut-badge" style={{ background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
};

const CommandesFournisseurs = () => {
  const { user } = useAuth();
  const { selectedBoutiqueId, boutiques } = useBoutique();

  const [commandes,    setCommandes]    = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits,     setProduits]     = useState([]);
  // Recherche produit (barre unique au-dessus des lignes de la commande)
  const [rechercheProduit, setRechercheProduit] = useState("");
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  const [rechercheIdx,     setRechercheIdx]     = useState(0);
  const rechercheRef = useRef(null);
  const [entrepots,    setEntrepots]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [toast,        setToast]        = useState({ type: "", text: "" });

  const [filterStatut,      setFilterStatut]      = useState("");
  const [filterFournisseur, setFilterFournisseur] = useState("");

  const [showModal,          setShowModal]          = useState(false);
  const [showReceptionModal, setShowReceptionModal] = useState(false);
  const [editMode,           setEditMode]           = useState(false);
  const [selectedCommande,   setSelectedCommande]   = useState(null);

  const [formData, setFormData] = useState({ fournisseur: "", commentaire: "", lignes: [] });
  const [receptionData, setReceptionData] = useState({
    destination: "entrepot",   // "entrepot" | "boutique"
    entrepot: "",
    boutiqueId: "",
    quantitesRecues: {}, montantPaye: "",
    modePaiement: "especes", commentairePaiement: "",
  });

  useEffect(() => { loadData(); }, [filterStatut, filterFournisseur]); // eslint-disable-line
  useEffect(() => { if (selectedBoutiqueId) setReceptionData(p => ({ ...p, boutiqueId: selectedBoutiqueId })); }, [selectedBoutiqueId]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: "", text: "" }), 3500);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatut)      params.statut      = filterStatut;
      if (filterFournisseur) params.fournisseur  = filterFournisseur;
      const [cr, fr, pr, er] = await Promise.all([
        getCommandes(params), getFournisseurs(true), getProduits(), getEntrepots()
      ]);
      setCommandes(cr.data.data || []);
      setFournisseurs(fr.data.data || []);
      setProduits(pr.data.data || []);
      setEntrepots(er.data.data || []);
    } catch { showToast("error", "Erreur lors du chargement"); }
    finally  { setLoading(false); }
  };

  /* ── Commande CRUD ── */
  const openCreateModal = () => {
    setEditMode(false); setSelectedCommande(null);
    setFormData({ fournisseur: "", commentaire: "", lignes: [] });
    setShowModal(true);
  };

  const openEditModal = (c) => {
    if (c.statut !== "brouillon") { showToast("error", "Seules les commandes en brouillon peuvent être modifiées"); return; }
    setEditMode(true); setSelectedCommande(c);
    setFormData({ fournisseur: c.fournisseur._id, commentaire: c.commentaire || "", lignes: c.lignes.map(l => ({ produit: l.produit._id, quantite: l.quantite, typeVente: l.typeVente, prixUnitaire: l.prixUnitaire })) });
    setShowModal(true);
  };

  const openReceptionModal = (c) => {
    if (!["envoyee", "livree_partielle"].includes(c.statut)) { showToast("error", "Cette commande ne peut pas être réceptionnée"); return; }
    setSelectedCommande(c);
    const qr = {};
    const rep = {};
    c.lignes.forEach(l => {
      const restant = l.quantite - (l.quantiteRecue || 0);
      qr[l._id] = restant;
      rep[l._id] = [{ entrepot: "", boutique: "", quantite: restant }];
    });
    setReceptionData({
      destination: "entrepot",
      entrepot: "",
      boutiqueId: "",
      quantitesRecues: qr,
      repartitions: rep,
      montantPaye: "",
      modePaiement: "especes",
      commentairePaiement: "",
    });
    setShowReceptionModal(true);
  };

  // Index de recherche : nom, référence, code-barres, champs personnalisés
  const indexProduits = useMemo(() => produits.map(p => ({
    p,
    txt: normaliser([p.nom, p.codeProduit, p.codeBarres,
      ...(p.champsPersonnalises || []).flatMap(c => [c.cle, c.valeur])].filter(Boolean).join(" ")),
  })), [produits]);

  const resultatsRecherche = useMemo(() => {
    const mots = normaliser(rechercheProduit).split(/\s+/).filter(Boolean);
    if (!mots.length) return [];
    return indexProduits.filter(x => mots.every(m => x.txt.includes(m))).slice(0, 30).map(x => x.p);
  }, [indexProduits, rechercheProduit]);

  // Ajoute le produit à la commande ; s'il y est déjà, +1 sur sa quantité
  const ajouterProduit = (p) => {
    setFormData(f => {
      const idx = f.lignes.findIndex(l => l.produit === p._id);
      if (idx >= 0) {
        return { ...f, lignes: f.lignes.map((l, i) => i === idx ? { ...l, quantite: (Number(l.quantite) || 0) + 1 } : l) };
      }
      return { ...f, lignes: [...f.lignes, { produit: p._id, quantite: 1, typeVente: p.typeVente || "detail", prixUnitaire: p.prixAchat || 0 }] };
    });
    setRechercheProduit("");
    setRechercheIdx(0);
    rechercheRef.current?.focus();
  };

  const onRechercheKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault(); setRechercheOuverte(true);
      setRechercheIdx(i => Math.min(i + 1, Math.max(resultatsRecherche.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setRechercheIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault(); // ne pas soumettre le formulaire
      const p = resultatsRecherche[rechercheIdx];
      if (p) ajouterProduit(p);
    } else if (e.key === "Escape") {
      if (rechercheProduit) { e.stopPropagation(); setRechercheProduit(""); }
      setRechercheOuverte(false);
    }
  };

  const modifierLigne = (i, field, value) => {
    const nl = [...formData.lignes];
    nl[i][field] = value;
    if (field === "produit" && value) {
      const p = produits.find(pr => pr._id === value);
      if (p) { nl[i].typeVente = p.typeVente || "detail"; nl[i].prixUnitaire = p.prixAchat || 0; }
    }
    setFormData(f => ({ ...f, lignes: nl }));
  };

  const supprimerLigne = (i) => setFormData(f => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fournisseur) { showToast("error", "Veuillez sélectionner un fournisseur"); return; }
    if (!formData.lignes.length) { showToast("error", "Veuillez ajouter au moins un produit"); return; }
    for (let i = 0; i < formData.lignes.length; i++) {
      const l = formData.lignes[i];
      if (!l.produit || !l.quantite || !l.prixUnitaire) { showToast("error", `Ligne ${i+1} : tous les champs sont requis`); return; }
    }
    setSubmitting(true);
    try {
      if (editMode) { await updateCommande(selectedCommande._id, formData); showToast("success", "Commande modifiée"); }
      else          { await createCommande(formData); showToast("success", "Commande créée"); }
      setShowModal(false); loadData();
    } catch (err) { showToast("error", err.response?.data?.message || "Erreur"); }
    finally { setSubmitting(false); }
  };

  const handleEnvoyer = async (id) => {
    if (!window.confirm("Envoyer cette commande au fournisseur ?")) return;
    try { await envoyerCommande(id); showToast("success", "Commande envoyée"); loadData(); }
    catch (err) { showToast("error", err.response?.data?.message || "Erreur envoi"); }
  };

  // ── Helpers répartition multi-entrepôts ──
  const setRepartitions = (ligneId, updater) => setReceptionData(p => ({
    ...p,
    repartitions: { ...p.repartitions, [ligneId]: updater(p.repartitions[ligneId] || []) },
  }));
  const addAllocation    = (ligneId)             => setRepartitions(ligneId, arr => [...arr, { entrepot: "", boutique: "", quantite: 0 }]);
  const removeAllocation = (ligneId, idx)        => setRepartitions(ligneId, arr => arr.filter((_, i) => i !== idx));
  const updateAllocation = (ligneId, idx, field, value) => setRepartitions(ligneId, arr =>
    arr.map((a, i) => i === idx ? { ...a, [field]: field === "quantite" ? (parseInt(value, 10) || 0) : value } : a));
  const totalReparti = (ligneId) => (receptionData.repartitions?.[ligneId] || []).reduce((s, a) => s + (Number(a.quantite) || 0), 0);

  const handleReception = async (e) => {
    e.preventDefault();
    const montantPaye = Number(receptionData.montantPaye) || 0;
    if (montantPaye < 0) { showToast("error", "Le montant ne peut pas être négatif"); return; }
    if (montantPaye > selectedCommande.montantTotal) { showToast("error", "Montant dépasse le total"); return; }

    let payload;
    if (receptionData.destination === "multi-boutique") {
      // Construire les répartitions par boutique + valider
      const rep = {};
      let aucune = true, depassement = false, boutiqueManquante = false;
      for (const l of selectedCommande.lignes) {
        const restant = l.quantite - (l.quantiteRecue || 0);
        const raw = receptionData.repartitions?.[l._id] || [];
        if (raw.some(a => Number(a.quantite) > 0 && !a.boutique)) boutiqueManquante = true;
        const entries = raw.filter(a => a.boutique && Number(a.quantite) > 0)
          .map(a => ({ boutique: a.boutique, quantite: Number(a.quantite) }));
        const tot = entries.reduce((s, a) => s + a.quantite, 0);
        if (tot > 0) aucune = false;
        if (tot > restant) depassement = true;
        if (entries.length) rep[l._id] = entries;
      }
      if (boutiqueManquante) { showToast("error", "Sélectionnez une boutique pour chaque quantité saisie"); return; }
      if (depassement)       { showToast("error", "Une quantité dépasse le restant à recevoir"); return; }
      if (aucune)            { showToast("error", "Indiquez au moins une boutique et une quantité"); return; }
      payload = {
        destination: "multi-boutique", repartitions: rep, montantPaye,
        modePaiement: receptionData.modePaiement, commentairePaiement: receptionData.commentairePaiement,
      };
    } else if (receptionData.destination === "multi") {
      // Construire les répartitions propres + valider
      const rep = {};
      let aucune = true, depassement = false, entrepotManquant = false;
      for (const l of selectedCommande.lignes) {
        const restant = l.quantite - (l.quantiteRecue || 0);
        const raw = receptionData.repartitions?.[l._id] || [];
        if (raw.some(a => Number(a.quantite) > 0 && !a.entrepot)) entrepotManquant = true;
        const entries = raw.filter(a => a.entrepot && Number(a.quantite) > 0)
          .map(a => ({ entrepot: a.entrepot, quantite: Number(a.quantite) }));
        const tot = entries.reduce((s, a) => s + a.quantite, 0);
        if (tot > 0) aucune = false;
        if (tot > restant) depassement = true;
        if (entries.length) rep[l._id] = entries;
      }
      if (entrepotManquant) { showToast("error", "Sélectionnez un entrepôt pour chaque quantité saisie"); return; }
      if (depassement)      { showToast("error", "Une quantité dépasse le restant à recevoir"); return; }
      if (aucune)           { showToast("error", "Indiquez au moins un entrepôt et une quantité"); return; }
      payload = {
        destination: "multi", repartitions: rep, montantPaye,
        modePaiement: receptionData.modePaiement, commentairePaiement: receptionData.commentairePaiement,
      };
    } else {
      if (receptionData.destination === "entrepot" && !receptionData.entrepot) {
        showToast("error", "Veuillez sélectionner un entrepôt"); return;
      }
      if (receptionData.destination === "boutique" && !receptionData.boutiqueId) {
        showToast("error", "Veuillez sélectionner une boutique"); return;
      }
      if (!Object.values(receptionData.quantitesRecues).some(q => q > 0)) { showToast("error", "Saisissez au moins une quantité"); return; }
      payload = { ...receptionData, repartitions: undefined, montantPaye };
    }

    setSubmitting(true);
    try {
      await recevoirCommande(selectedCommande._id, payload);
      showToast("success", "Réception enregistrée"); setShowReceptionModal(false); loadData();
    } catch (err) { showToast("error", err.response?.data?.message || "Erreur réception"); }
    finally { setSubmitting(false); }
  };

  const handleAnnuler = async (id) => {
    if (!window.confirm("Annuler cette commande ?")) return;
    try { await annulerCommande(id); showToast("success", "Commande annulée"); loadData(); }
    catch (err) { showToast("error", err.response?.data?.message || "Erreur annulation"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cette commande ?")) return;
    try { await deleteCommande(id); showToast("success", "Commande supprimée"); loadData(); }
    catch (err) { showToast("error", err.response?.data?.message || "Erreur suppression"); }
  };

  const totalMontant = commandes.reduce((s, c) => s + (c.montantTotal || 0), 0);
  const nbBrouillon  = commandes.filter(c => c.statut === "brouillon").length;
  const nbEnCours    = commandes.filter(c => ["envoyee","livree_partielle"].includes(c.statut)).length;
  const nbLivrees    = commandes.filter(c => c.statut === "livree").length;

  const montantTotal = formData.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaire, 0);

  // ── Simulation de bénéfice : si on revend tout le stock commandé ──
  const prixVenteLigne = (l) => {
    const p = produits.find(pr => pr._id === l.produit);
    if (!p) return 0;
    if (l.typeVente === "gros") return p.prixVenteGros || p.prixVente || 0;
    return p.prixVenteDetail || p.prixVente || 0;
  };
  const simulation = formData.lignes.reduce((acc, l) => {
    const qte     = Number(l.quantite) || 0;
    const cout    = qte * (Number(l.prixUnitaire) || 0);
    const revente = qte * prixVenteLigne(l);
    acc.cout    += cout;
    acc.revente += revente;
    return acc;
  }, { cout: 0, revente: 0 });
  const beneficeSim = simulation.revente - simulation.cout;
  const margeSim    = simulation.revente > 0 ? (beneficeSim / simulation.revente) * 100 : 0;

  if (loading) return (
    <div className="cf-loading"><Loader className="cf-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="cf-page">

      {/* Toast */}
      {toast.text && (
        <div className={`cf-toast cf-toast--${toast.type}`}>
          {toast.type === "success" ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="cf-header">
        <div className="cf-header-left">
          <div className="cf-header-icon"><ShoppingCart size={20}/></div>
          <div>
            <h1 className="cf-header-title">Commandes Fournisseurs</h1>
            <p className="cf-header-sub">Gérer les commandes d'approvisionnement</p>
          </div>
        </div>
        <div className="cf-header-right">
          {["admin-entreprise","admin-pro"].includes(user?.role) && <BoutiqueSelector/>}
          <button className="cf-cta-btn" onClick={openCreateModal}>
            <Plus size={14}/> Nouvelle commande
          </button>
        </div>
      </div>

      <div className="cf-content">

        {/* KPI row */}
        <div className="cf-kpi-row">
          {[
            { label: "Total commandes", value: commandes.length,     color: "#6366f1", bg: "#eef2ff",  icon: ShoppingCart },
            { label: "Brouillons",      value: nbBrouillon,           color: "#64748b", bg: "#f1f5f9",  icon: Clock },
            { label: "En cours",        value: nbEnCours,             color: "#d97706", bg: "#fffbeb",  icon: TrendingUp },
            { label: "Livrées",         value: nbLivrees,             color: "#16a34a", bg: "#dcfce7",  icon: Package },
            { label: "Montant total",   value: `${fmt(totalMontant)} XOF`, color: "#f59e0b", bg: "#fffbeb", icon: ChevronRight },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="cf-kpi" style={{ borderTopColor: k.color }}>
                <div className="cf-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={15}/></div>
                <div className="cf-kpi-val">{k.value}</div>
                <div className="cf-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="cf-filters">
          <Filter size={14} style={{ color: "#94a3b8", flexShrink: 0 }}/>
          <select className="cf-select" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="brouillon">Brouillon</option>
            <option value="envoyee">Envoyée</option>
            <option value="livree_partielle">Livrée partiellement</option>
            <option value="livree">Livrée</option>
            <option value="annulee">Annulée</option>
          </select>
          <select className="cf-select" value={filterFournisseur} onChange={e => setFilterFournisseur(e.target.value)}>
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.nom}</option>)}
          </select>
          <span className="cf-count">{commandes.length} commande{commandes.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Table */}
        {commandes.length === 0 ? (
          <div className="cf-empty">
            <div className="cf-empty-icon"><ShoppingCart size={30}/></div>
            <h3>Aucune commande</h3>
            <p>Créez votre première commande fournisseur</p>
            <button className="cf-cta-btn" onClick={openCreateModal}><Plus size={13}/> Nouvelle commande</button>
          </div>
        ) : (
          <div className="cf-card">
            <table className="cf-table">
              <thead>
                <tr>
                  <th>N° commande</th>
                  <th>Fournisseur</th>
                  <th>Date</th>
                  <th>Articles</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commandes.map(c => (
                  <tr key={c._id} className={c.statut === "annulee" ? "cf-row--dim" : ""}>
                    <td>
                      <div className="cf-num-cell">
                        <span className="cf-num">{c.numeroCommande}</span>
                        {c.statut === "livree_partielle" && (
                          <span className="cf-partial-warn">⚠ Partielle</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="cf-supplier-cell">
                        <div className="cf-supplier-av">{c.fournisseur?.nom?.charAt(0) || "F"}</div>
                        <span>{c.fournisseur?.nom}</span>
                      </div>
                    </td>
                    <td className="cf-date">{moment(c.dateCommande).format("DD/MM/YYYY")}</td>
                    <td>
                      <span className="cf-article-count">{c.lignes.length} art.</span>
                    </td>
                    <td className="cf-amount">{fmt(c.montantTotal)} <small>XOF</small></td>
                    <td><StatutBadge statut={c.statut}/></td>
                    <td>
                      <div className="cf-actions">
                        {c.statut === "brouillon" && (
                          <>
                            <button className="cf-ico-btn cf-ico-btn--edit" onClick={() => openEditModal(c)} title="Modifier"><Edit2 size={13}/></button>
                            <button className="cf-action-btn cf-action-btn--send" onClick={() => handleEnvoyer(c._id)} title="Envoyer">
                              <Send size={12}/> Envoyer
                            </button>
                            <button className="cf-ico-btn cf-ico-btn--del" onClick={() => handleDelete(c._id)} title="Supprimer"><Trash2 size={13}/></button>
                          </>
                        )}
                        {["envoyee","livree_partielle"].includes(c.statut) && (
                          <>
                            <button className="cf-action-btn cf-action-btn--receive" onClick={() => openReceptionModal(c)}>
                              <Package size={12}/> Recevoir
                            </button>
                            <button className="cf-ico-btn cf-ico-btn--del" onClick={() => handleAnnuler(c._id)} title="Annuler"><Ban size={13}/></button>
                          </>
                        )}
                        {c.statut === "livree" && (
                          <span className="cf-done-label"><Check size={12}/> Complète</span>
                        )}
                        {c.statut === "annulee" && (
                          <span className="cf-cancelled-label"><X size={12}/> Annulée</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          Modal Création / Modification
         ══════════════════════════════════════ */}
      {showModal && (
        <>
          <div className="cf-overlay" onClick={() => setShowModal(false)}/>
          <div className="cf-modal cf-modal--wide">
            <div className="cf-modal-head">
              <div>
                <h2 className="cf-modal-title">{editMode ? "Modifier la commande" : "Nouvelle commande"}</h2>
                <p className="cf-modal-sub">{editMode ? selectedCommande?.numeroCommande : "Renseigner le fournisseur et les produits à commander"}</p>
              </div>
              <button className="cf-modal-close" onClick={() => setShowModal(false)}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit} className="cf-modal-body">
              <div className="cf-form-row">
                <div className="cf-field cf-field--grow">
                  <label>Fournisseur *</label>
                  <select value={formData.fournisseur} onChange={e => setFormData({...formData, fournisseur: e.target.value})} required>
                    <option value="">Sélectionner un fournisseur</option>
                    {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.nom}</option>)}
                  </select>
                </div>
                <div className="cf-field cf-field--grow">
                  <label>Commentaire</label>
                  <input type="text" value={formData.commentaire} onChange={e => setFormData({...formData, commentaire: e.target.value})} placeholder="Note sur la commande…"/>
                </div>
              </div>

              {/* Lignes */}
              <div className="cf-lignes-head">
                <span className="cf-section-label">Produits</span>
                {formData.lignes.length > 0 && (
                  <span className="cf-lignes-count">{formData.lignes.length} ligne{formData.lignes.length > 1 ? "s" : ""}</span>
                )}
              </div>

              {/* Recherche produit : un clic / Entrée ajoute le produit à la commande */}
              <div className="cf-ps">
                <div className={`cf-ps-bar${rechercheOuverte && rechercheProduit ? " cf-ps-bar--open" : ""}`}>
                  <Search size={16} className="cf-ps-ico"/>
                  <input
                    ref={rechercheRef}
                    type="text"
                    className="cf-ps-input"
                    placeholder="Rechercher un produit à ajouter : nom, référence, code-barres…"
                    value={rechercheProduit}
                    onChange={e => { setRechercheProduit(e.target.value); setRechercheOuverte(true); setRechercheIdx(0); }}
                    onFocus={() => setRechercheOuverte(true)}
                    onBlur={() => setTimeout(() => setRechercheOuverte(false), 150)}
                    onKeyDown={onRechercheKeyDown}
                    autoComplete="off"
                  />
                  {rechercheProduit ? (
                    <button type="button" className="cf-ps-clear" title="Effacer"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setRechercheProduit(""); rechercheRef.current?.focus(); }}>
                      <X size={13}/>
                    </button>
                  ) : (
                    <span className="cf-ps-hint">↑↓ puis Entrée</span>
                  )}
                </div>

                {rechercheOuverte && rechercheProduit.trim() && (
                  <div className="cf-ps-results" onMouseDown={e => e.preventDefault()}>
                    {resultatsRecherche.length === 0 ? (
                      <div className="cf-ps-empty"><Package size={18}/> Aucun produit trouvé pour « {rechercheProduit.trim()} »</div>
                    ) : resultatsRecherche.map((p, idx) => {
                      const stock = Number(p.quantiteStock) || 0;
                      const seuil = p.seuilAlerte != null ? Number(p.seuilAlerte) : 10;
                      const ligne = formData.lignes.find(l => l.produit === p._id);
                      return (
                        <div key={p._id}
                          className={`cf-ps-item${idx === rechercheIdx ? " cf-ps-item--hl" : ""}`}
                          onMouseEnter={() => setRechercheIdx(idx)}
                          onClick={() => ajouterProduit(p)}>
                          <div className="cf-ps-av">{(p.nom || "?").charAt(0).toUpperCase()}</div>
                          <div className="cf-ps-main">
                            <div className="cf-ps-nom">{p.nom}</div>
                            <div className="cf-ps-meta">
                              {p.codeProduit}{p.codeBarres ? ` · ${p.codeBarres}` : ""}
                              {p.prixAchat ? ` · achat ${fmt(p.prixAchat)} XOF` : ""}
                            </div>
                          </div>
                          <span className={`cf-ps-stock${stock <= 0 ? " cf-ps-stock--out" : stock <= seuil ? " cf-ps-stock--low" : ""}`}>
                            {stock <= 0 ? "Rupture" : `Stock ${stock}`}
                          </span>
                          {ligne
                            ? <span className="cf-ps-added"><Check size={12}/> ×{ligne.quantite}</span>
                            : <span className="cf-ps-add"><Plus size={12}/></span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {formData.lignes.length === 0 ? (
                <div className="cf-lignes-empty">
                  <Package size={20}/>
                  <span>Aucun produit — recherchez un produit ci-dessus pour l'ajouter</span>
                </div>
              ) : (
                <>
                  <div className="cf-lignes-table-wrap">
                    <table className="cf-lignes-table">
                      <thead>
                        <tr><th>Produit</th><th>Qté</th><th>Type</th><th>Prix unitaire</th><th>Montant</th><th></th></tr>
                      </thead>
                      <tbody>
                        {formData.lignes.map((ligne, i) => {
                          const produitSelectionne = produits.find(p => p._id === ligne.produit);
                          return (
                          <tr key={i}>
                            <td>
                              {produitSelectionne ? (
                                <div className="cf-ligne-produit">
                                  <span className="cf-ligne-produit-nom">{produitSelectionne.nom}</span>
                                  <span className="cf-ligne-produit-code">{produitSelectionne.codeProduit}</span>
                                </div>
                              ) : (
                                <span className="cf-ligne-produit-code">Produit introuvable</span>
                              )}
                              {/* Champ caché pour validation HTML required */}
                              <input type="hidden" value={ligne.produit} required/>
                            </td>
                            <td>
                              <input type="number" min="1" value={ligne.quantite} onChange={e => modifierLigne(i, "quantite", parseInt(e.target.value)||0)} required/>
                            </td>
                            <td>
                              <select value={ligne.typeVente} onChange={e => modifierLigne(i, "typeVente", e.target.value)}>
                                <option value="detail">Détail</option>
                                <option value="gros">Gros</option>
                              </select>
                            </td>
                            <td>
                              <input type="number" min="0" value={ligne.prixUnitaire} onChange={e => modifierLigne(i, "prixUnitaire", parseFloat(e.target.value)||0)} required/>
                            </td>
                            <td className="cf-ligne-amount">
                              {fmt(ligne.quantite * ligne.prixUnitaire)} XOF
                              {(() => {
                                const pv = prixVenteLigne(ligne);
                                if (!pv) return null;
                                const benef = ligne.quantite * (pv - ligne.prixUnitaire);
                                return (
                                  <div className={`cf-ligne-benef ${benef >= 0 ? 'cf-benef-pos' : 'cf-benef-neg'}`}>
                                    Bénéf. {benef >= 0 ? '+' : ''}{fmt(benef)}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>
                              <button type="button" className="cf-ico-btn cf-ico-btn--del" onClick={() => supprimerLigne(i)}><X size={12}/></button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="cf-total-bar">
                    <span>Total commande</span>
                    <strong>{fmt(montantTotal)} XOF</strong>
                  </div>

                  {/* ── Simulation de bénéfice ── */}
                  <div className="cf-sim">
                    <div className="cf-sim-head">
                      <TrendingUp size={14}/>
                      <span>Simulation — bénéfice si tout le stock est revendu</span>
                    </div>
                    <div className="cf-sim-grid">
                      <div className="cf-sim-cell">
                        <span className="cf-sim-lbl">Coût d'achat</span>
                        <span className="cf-sim-val">{fmt(simulation.cout)} XOF</span>
                      </div>
                      <div className="cf-sim-cell">
                        <span className="cf-sim-lbl">Revente estimée</span>
                        <span className="cf-sim-val">{fmt(simulation.revente)} XOF</span>
                      </div>
                      <div className="cf-sim-cell">
                        <span className="cf-sim-lbl">Bénéfice estimé</span>
                        <span className={`cf-sim-val ${beneficeSim >= 0 ? 'cf-benef-pos' : 'cf-benef-neg'}`}>
                          {beneficeSim >= 0 ? '+' : ''}{fmt(beneficeSim)} XOF
                        </span>
                      </div>
                      <div className="cf-sim-cell">
                        <span className="cf-sim-lbl">Marge moyenne</span>
                        <span className={`cf-sim-val ${beneficeSim >= 0 ? 'cf-benef-pos' : 'cf-benef-neg'}`}>
                          {margeSim.toFixed(1)} %
                        </span>
                      </div>
                    </div>
                    <p className="cf-sim-note">Basé sur le prix de vente actuel de chaque produit ({"gros"} ou détail selon le type de ligne). Indicatif — hors remises et invendus.</p>
                  </div>
                </>
              )}

              <div className="cf-modal-footer">
                <button type="button" className="cf-btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="cf-btn-confirm" disabled={submitting}>
                  {submitting ? <><Loader size={13} className="cf-spin"/> Enregistrement…</> : <><Check size={13}/> {editMode ? "Enregistrer" : "Créer la commande"}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════
          Modal Réception
         ══════════════════════════════════════ */}
      {showReceptionModal && selectedCommande && (
        <>
          <div className="cf-overlay" onClick={() => setShowReceptionModal(false)}/>
          <div className="cf-modal cf-modal--wide">
            <div className="cf-modal-head cf-modal-head--receive">
              <div>
                <h2 className="cf-modal-title"><Package size={16}/> Réceptionner la commande</h2>
                <p className="cf-modal-sub">{selectedCommande.numeroCommande} · {selectedCommande.fournisseur?.nom}</p>
              </div>
              <button className="cf-modal-close" onClick={() => setShowReceptionModal(false)}><X size={16}/></button>
            </div>

            <form onSubmit={handleReception} className="cf-modal-body">

              {/* Toggle destination */}
              <div className="cf-dest-row">
                <span className="cf-dest-label">Réceptionner dans *</span>
                <div className="cf-dest-choices">
                  {[
                    { value: "entrepot",       label: "Un entrepôt" },
                    { value: "multi",          label: "Plusieurs entrepôts" },
                    { value: "boutique",       label: "Une boutique" },
                    { value: "multi-boutique", label: "Plusieurs boutiques" },
                  ].map(opt => (
                    <label key={opt.value}
                      className={`cf-dest-choice${receptionData.destination === opt.value ? " cf-dest-choice--active" : ""}`}>
                      <input type="radio" name="recepDest" value={opt.value}
                        checked={receptionData.destination === opt.value}
                        onChange={() => setReceptionData(p => ({
                          ...p, destination: opt.value, entrepot: "", boutiqueId: "",
                        }))}/>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {receptionData.destination === "entrepot" && (
                <div className="cf-field" style={{marginBottom:14}}>
                  <label>Entrepôt *</label>
                  <select value={receptionData.entrepot}
                    onChange={e => setReceptionData({...receptionData, entrepot: e.target.value})} required>
                    <option value="">Sélectionner un entrepôt</option>
                    {entrepots.filter(e => e.actif).map(e => (
                      <option key={e._id} value={e._id}>{e.nom}</option>
                    ))}
                  </select>
                  {entrepots.filter(e => e.actif).length === 0 && (
                    <p className="cf-warn-text"><AlertCircle size={12}/> Aucun entrepôt actif disponible</p>
                  )}
                </div>
              )}
              {receptionData.destination === "boutique" && (
                <div className="cf-field" style={{marginBottom:14}}>
                  <label>Boutique *</label>
                  <select value={receptionData.boutiqueId}
                    onChange={e => setReceptionData({...receptionData, boutiqueId: e.target.value})} required>
                    <option value="">Sélectionner une boutique</option>
                    {boutiques.map(b => (
                      <option key={b._id} value={b._id}>{b.nom}</option>
                    ))}
                  </select>
                </div>
              )}
              {receptionData.destination === "multi" && (
                <p className="cf-payment-info" style={{marginBottom:14}}>
                  <AlertCircle size={13}/> Répartissez la quantité reçue de chaque produit sur un ou plusieurs entrepôts.
                </p>
              )}
              {receptionData.destination === "multi-boutique" && (
                <p className="cf-payment-info" style={{marginBottom:14}}>
                  <AlertCircle size={13}/> Répartissez la quantité reçue de chaque produit sur une ou plusieurs boutiques. Le produit (même code) doit déjà exister dans chaque boutique choisie.
                </p>
              )}

              {/* Quantités reçues */}
              <div className="cf-section-label" style={{marginTop:8}}>Quantités reçues</div>
              <div className="cf-reception-list">
                {selectedCommande.lignes.map(l => {
                  const restant = l.quantite - (l.quantiteRecue || 0);
                  const actifs = entrepots.filter(e => e.actif);
                  if (receptionData.destination === "multi") {
                    const allocs = receptionData.repartitions?.[l._id] || [];
                    const tot = totalReparti(l._id);
                    const depasse = tot > restant;
                    return (
                      <div key={l._id} className="cf-reception-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                        <div className="cf-reception-info">
                          <strong>{l.produit?.nom || "—"}</strong>
                          <span>
                            Commandé : {l.quantite} {l.typeVente === "gros" ? "carton(s)" : "unité(s)"}
                            {l.quantiteRecue > 0 ? ` · Déjà reçu : ${l.quantiteRecue}` : ""}
                            {" · "}<strong style={{ color: depasse ? "#dc2626" : "#16a34a" }}>{tot}</strong> / {restant} restant{restant > 1 ? "s" : ""}
                          </span>
                        </div>
                        {allocs.map((a, idx) => (
                          <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <select value={a.entrepot}
                              onChange={e => updateAllocation(l._id, idx, "entrepot", e.target.value)}
                              style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}>
                              <option value="">Entrepôt…</option>
                              {actifs.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                            </select>
                            <input type="number" min="0" max={restant} value={a.quantite}
                              onChange={e => updateAllocation(l._id, idx, "quantite", e.target.value)}
                              style={{ width: 80, padding: "7px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}/>
                            {allocs.length > 1 && (
                              <button type="button" onClick={() => removeAllocation(l._id, idx)}
                                style={{ width: 28, height: 28, border: "none", background: "#fee2e2", color: "#dc2626", borderRadius: 6, cursor: "pointer" }}>×</button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={() => addAllocation(l._id)}
                          style={{ alignSelf: "flex-start", background: "#fff", border: "1.5px solid #c7d2fe", color: "#6366f1", borderRadius: 7, fontSize: 12, fontWeight: 700, padding: "5px 10px", cursor: "pointer" }}>
                          + Ajouter un entrepôt
                        </button>
                      </div>
                    );
                  }
                  if (receptionData.destination === "multi-boutique") {
                    const allocs = receptionData.repartitions?.[l._id] || [];
                    const tot = totalReparti(l._id);
                    const depasse = tot > restant;
                    return (
                      <div key={l._id} className="cf-reception-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                        <div className="cf-reception-info">
                          <strong>{l.produit?.nom || "—"}</strong>
                          <span>
                            Commandé : {l.quantite} {l.typeVente === "gros" ? "carton(s)" : "unité(s)"}
                            {l.quantiteRecue > 0 ? ` · Déjà reçu : ${l.quantiteRecue}` : ""}
                            {" · "}<strong style={{ color: depasse ? "#dc2626" : "#16a34a" }}>{tot}</strong> / {restant} restant{restant > 1 ? "s" : ""}
                          </span>
                        </div>
                        {allocs.map((a, idx) => (
                          <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <select value={a.boutique || ""}
                              onChange={e => updateAllocation(l._id, idx, "boutique", e.target.value)}
                              style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}>
                              <option value="">Boutique…</option>
                              {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                            </select>
                            <input type="number" min="0" max={restant} value={a.quantite}
                              onChange={e => updateAllocation(l._id, idx, "quantite", e.target.value)}
                              style={{ width: 80, padding: "7px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}/>
                            {allocs.length > 1 && (
                              <button type="button" onClick={() => removeAllocation(l._id, idx)}
                                style={{ width: 28, height: 28, border: "none", background: "#fee2e2", color: "#dc2626", borderRadius: 6, cursor: "pointer" }}>×</button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={() => addAllocation(l._id)}
                          style={{ alignSelf: "flex-start", background: "#fff", border: "1.5px solid #c7d2fe", color: "#6366f1", borderRadius: 7, fontSize: 12, fontWeight: 700, padding: "5px 10px", cursor: "pointer" }}>
                          + Ajouter une boutique
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div key={l._id} className="cf-reception-row">
                      <div className="cf-reception-info">
                        <strong>{l.produit?.nom || "—"}</strong>
                        <span>
                          Commandé : {l.quantite} {l.typeVente === "gros" ? "carton(s)" : "unité(s)"}
                          {l.quantiteRecue > 0 ? ` · Déjà reçu : ${l.quantiteRecue}` : ""}
                        </span>
                      </div>
                      <div className="cf-reception-qty">
                        <input type="number" min="0" max={restant}
                          value={receptionData.quantitesRecues[l._id] ?? 0}
                          onChange={e => setReceptionData(p => ({
                            ...p,
                            quantitesRecues: {
                              ...p.quantitesRecues,
                              [l._id]: parseInt(e.target.value) || 0
                            }
                          }))}/>
                        <span className="cf-reception-max">/ {restant} restant{restant > 1 ? "s" : ""}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paiement */}
              <div className="cf-section-label" style={{marginTop:16}}>Paiement fournisseur</div>
              <div className="cf-payment-info">
                <AlertCircle size={13}/>
                Le reste non payé sera enregistré comme dette fournisseur.
              </div>

              <div className="cf-form-row">
                <div className="cf-field cf-field--grow">
                  <label>Montant payé maintenant (XOF)</label>
                  <input type="number" min="0" max={selectedCommande.montantTotal}
                    value={receptionData.montantPaye}
                    onChange={e => setReceptionData({...receptionData, montantPaye: e.target.value})}
                    placeholder="0"/>
                </div>
                <div className="cf-field cf-field--grow">
                  <label>Mode de paiement</label>
                  <select value={receptionData.modePaiement} onChange={e => setReceptionData({...receptionData, modePaiement: e.target.value})}>
                    <option value="especes">Espèces</option>
                    <option value="wave">Wave</option>
                    <option value="orange_money">Orange Money</option>
                    <option value="carte_bancaire">Carte bancaire</option>
                    <option value="virement">Virement</option>
                    <option value="cheque">Chèque</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="cf-field">
                <label>Commentaire paiement</label>
                <input type="text" value={receptionData.commentairePaiement}
                  onChange={e => setReceptionData({...receptionData, commentairePaiement: e.target.value})}
                  placeholder="Note facultative…"/>
              </div>

              {/* Summary */}
              <div className="cf-payment-summary">
                <div className="cf-pay-row">
                  <span>Montant de la commande</span>
                  <strong>{fmt(selectedCommande.montantTotal)} XOF</strong>
                </div>
                <div className="cf-pay-row cf-pay-row--debt">
                  <span>Reste à payer (dette)</span>
                  <strong>{fmt(Math.max(selectedCommande.montantTotal - (Number(receptionData.montantPaye)||0), 0))} XOF</strong>
                </div>
              </div>

              <div className="cf-modal-footer">
                <button type="button" className="cf-btn-cancel" onClick={() => setShowReceptionModal(false)}>Annuler</button>
                <button type="submit" className="cf-btn-confirm cf-btn-confirm--receive" disabled={submitting}>
                  {submitting ? <><Loader size={13} className="cf-spin"/> Enregistrement…</> : <><Package size={13}/> Valider la réception</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default CommandesFournisseurs;
